import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  ThemeProvider,
  createTheme,
  CssBaseline
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import logo from '../assets/logo.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1B365D' },
    secondary: { main: '#78B6CB' },
    background: { default: '#f5f5f5', paper: '#ffffff' }
  }
});

export default function AlterarSenha({ required = false, onSuccess }) {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, changePassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // No primeiro acesso (required) não pedimos senha atual; depois, é obrigatória
    if (!required && !senhaAtual.trim()) {
      setError('Informe sua senha atual.');
      return;
    }

    if (novaSenha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setError('As senhas não coincidem.');
      return;
    }

    try {
      setLoading(true);
      setSuccess('');
      // No primeiro acesso não envia senha atual (usuário acabou de logar com a provisória)
      await changePassword(currentUser.uid, novaSenha, required ? null : senhaAtual);

      // Remove o flag mustChangePassword do Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { mustChangePassword: false });

      setSuccess('Senha alterada com sucesso.');
      // Dá tempo para o usuário ver o balão de sucesso
      await new Promise((resolve) => setTimeout(resolve, 1200));
      if (required) {
        onSuccess?.();
        navigate('/', { replace: true });
      } else {
        navigate(-1);
      }
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      if (
        err?.code === 'auth/wrong-password' ||
        err?.code === 'auth/invalid-credential' ||
        err?.code === 'auth/invalid-login-credentials'
      ) {
        setError('Senha atual incorreta.');
        return;
      }
      if (err?.code === 'auth/requires-recent-login') {
        if (required) {
          setError('Não foi possível alterar a senha neste momento. Faça logout, entre novamente com a senha provisória recebida por e-mail e defina sua nova senha na tela que aparecer.');
        } else {
          setError('Por segurança, informe sua senha atual e tente novamente.');
        }
        return;
      }
      setError('Não foi possível alterar a senha. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (required) {
      // Se for obrigatório, não pode cancelar - faz logout
      return;
    }
    navigate(-1);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 8
        }}
      >
        <Container component="main" maxWidth="xs">
          <Paper
            elevation={2}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <img
              src={logo}
              alt="Habitop Logo"
              style={{ height: '80px', width: 'auto', marginBottom: '1.5rem' }}
            />
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography component="h1" variant="h5" color="primary" gutterBottom>
              {required ? 'Altere sua senha' : 'Alterar senha'}
            </Typography>
            {required && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Por segurança, altere sua senha provisória antes de continuar.
              </Typography>
            )}
            {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              {/* Senha atual só aparece quando NÃO é primeiro acesso (alteração voluntária nas configurações) */}
              {!required && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="senhaAtual"
                  label="Senha atual"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={senhaAtual}
                  onChange={(e) => setSenhaAtual(e.target.value)}
                />
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                name="novaSenha"
                label="Nova senha"
                type="password"
                autoComplete="new-password"
                autoFocus={required}
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                helperText="Mínimo 6 caracteres"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmarSenha"
                label="Confirmar nova senha"
                type="password"
                autoComplete="new-password"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                {!required && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  {loading ? 'Salvando...' : 'Alterar senha'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
